import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { navigate } from 'gatsby';
import Link from 'components/UI/Link';
import SubMenu from '../SubMenu';
import { useHistoryCallback } from 'utils/routing';
import { getMousePosition, getRectangle, getTriangleZone, isInsideTriangle } from './functions';
import { pushHeaderEvent } from '../../utils';
import { observer, searchStore as store } from 'store';

import * as styles from './MainNav.module.scss';

const handleClickItem = item => {
  pushHeaderEvent('click_on_menu_item', item, 'Desktop');
};

const ItemWrapper = ({ item, children, ...props }) => {
  if (item?.to) {
    return (
      <Link
        href={item.to}
        state={{ activeTab: item.key }}
        onClick={() => handleClickItem(item.key)}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return <div {...props}>{children}</div>;
};

const hoverDelay = 100;

function MainNav({ items = [], mods = [], currentLink, isIndexPage }) {
  const [openTab, setOpenTab] = useState();
  const [withTransition, setWithTransition] = useState(true);
  const [inArea, setInArea] = useState(false);
  const [openPosition, setOpenPosition] = useState(null);
  const timeoutRef = useRef(null);
  const [pendingOpenTab, setPendingOpenTab] = useState(null);
  const [touchDevice, setTouchDevice] = useState(false);
  const menuRef = useRef(null);
  const subMenus = useRef([]);

  const checkAim = (menuElem, currentPosition) => {
    if (!openPosition) return false;
    const menuBox = getRectangle(menuElem);
    const triangle = getTriangleZone(menuBox, openPosition);

    return isInsideTriangle(triangle, currentPosition);
  };

  const clearPendingOpen = () => {
    if (pendingOpenTab && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setPendingOpenTab(null);
  };

  const updateOpen = () => {
    if (!pendingOpenTab || openTab === pendingOpenTab) {
      return;
    }
    const el = subMenus.current[pendingOpenTab].getBoundingClientRect();
    const newPosition = { x: el.x + el.width / 2, y: el.height * 0.25 };
    setOpenPosition(newPosition);
    setOpenTab(pendingOpenTab);
    clearPendingOpen();
  };

  const onItemEnter = (item, withSubmenu) => e => {
    if (touchDevice) {
      setTouchDevice(false);
    }

    if (withSubmenu && openTab) {
      setWithTransition(false);
    }

    clearPendingOpen();
    setPendingOpenTab(item);
    store.setDesktopMenuOpen(true);
    if (!touchDevice) {
      pushHeaderEvent('hover_on_menu_item', item, 'Desktop');
    }
  };

  const onItemLeave = () => {
    if (!inArea) {
      setPendingOpenTab(null);
      store.setDesktopMenuOpen(false);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(updateOpen, hoverDelay + 100);
    }
  };

  useEffect(() => {
    if (!touchDevice) {
      if (!openTab || !inArea) {
        updateOpen();
      } else {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(updateOpen, hoverDelay);
      }
    }
  }, [pendingOpenTab, openTab, inArea, hoverDelay, touchDevice]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const onMouseMove = event => {
    if (checkAim(menuRef.current, getMousePosition(event.nativeEvent))) {
      setInArea(true);
    } else {
      setInArea(false);
    }
  };

  const closeMenu = () => {
    clearPendingOpen();
    setOpenTab(null);
    setWithTransition(true);
    setTouchDevice(false);
    store.setDesktopMenuOpen(false);
  };

  // tablet logic
  const handleTabOpen = (tab, withSubmenu) => e => {
    const touch = e.type === 'touchstart';
    setTouchDevice(touch);

    if (withSubmenu && openTab && tab) {
      setWithTransition(false);
    } else {
      setWithTransition(true);
    }

    const shouldClose = !withSubmenu || (touch && e.target.id === tab && openTab === tab);
    if (shouldClose) {
      setOpenTab();
      store.setDesktopMenuOpen(false);
      return;
    }

    store.setDesktopMenuOpen(true);
    setOpenTab(tab);
    handleClickItem(tab);
  };

  const handleMenuItemKeyDown = item => e => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (item?.to) {
        handleClickItem(item.key);
        navigate(item.to, { state: { activeTab: item.key } });
      }
      e.preventDefault();
    }
  };

  const handleArrowKeyDown = (item, isHovered) => e => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpenTab(isHovered ? null : item.key);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpenTab(null);
      e.preventDefault();
    }
  };

  useHistoryCallback(closeMenu);

  return (
    <div
      ref={menuRef}
      className={cn(styles.mainNav, ...mods.map(key => styles[key]))}
      onMouseLeave={closeMenu}
      onMouseMove={onMouseMove}
      aria-label="Main navigation"
    >
      <div className={styles.list}>
        {items.map(item => {
          const isActive = getActiveTab(item.submenu, currentLink) || currentLink.includes(item.to);
          const isHovered = openTab === item.key;
          const withSubmenu = Boolean(item?.submenu);

          return (
            <div
              ref={el => (subMenus.current[item.key] = el)}
              id={item.key}
              key={item.key}
              className={cn(
                styles.item,
                withSubmenu && styles.submenu,
                isHovered && styles.showSubMenu,
              )}
              onTouchStart={handleTabOpen(item.key, withSubmenu)}
              onMouseEnter={onItemEnter(item.key, withSubmenu)}
              onMouseLeave={onItemLeave}
            >
              <ItemWrapper
                className={cn(
                  styles.link,
                  withSubmenu && styles.arrow,
                  withSubmenu && styles.touchDisabled,
                  !isIndexPage && isActive && styles.current,
                )}
                item={item}
                tabIndex={-1}
              >
                <span
                  className={styles.linkName}
                  tabIndex={0}
                  onKeyDown={handleMenuItemKeyDown(item)}
                >
                  {item.title}
                </span>
                {withSubmenu && (
                  <div
                    className={cn(styles.arrowIcon, styles.arrowRotated)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open ${item.key} submenu`}
                    aria-expanded={isHovered}
                    aria-haspopup="menu"
                    onKeyDown={handleArrowKeyDown(item, isHovered)}
                    onClick={e => {
                      setOpenTab(isHovered ? null : item.key);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                )}
              </ItemWrapper>
              {withSubmenu && (
                <SubMenu
                  active={isHovered}
                  theme={item.options?.theme}
                  parentLink={item?.parentLink}
                  currentLink={currentLink}
                  items={item.submenu}
                  withTransition={withTransition}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getActiveTab(submenuItems, currentLink) {
  if (!submenuItems) return;

  const submenuLinks = submenuItems?.reduce((acc, item) => {
    if (item.url) {
      acc.push(item.url);
    }

    if (item.parentLink) {
      acc.push(item.parentLink.url);
    }

    if (item.linkList) {
      item.linkList.forEach(link => acc.push(link.url));
    }

    return acc;
  }, []);

  return submenuLinks.includes(currentLink);
}

export default observer(MainNav);
